import React, { useState, useRef, useCallback } from 'react';
import { LinkedinLogo, TiktokLogo, Briefcase, ChartLineUp, Brain, TrendUp, ShieldCheck, Scales, ShoppingCart, PaperPlaneTilt, SpinnerGap, CheckCircle, Warning, Cpu, Lightbulb, ArrowSquareOut, ImageSquare } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import usePageMeta from '../hooks/usePageMeta';
import usePageSchema from '../hooks/usePageSchema';
import useAboutContent from '../hooks/useAboutContent';
import useI18n from '../hooks/useI18n';
import useScrollReveal from '../hooks/useScrollReveal';
import useSectionTone from '../hooks/useSectionTone';
import useScramble from '../hooks/useScramble';
import { FAQ_ABOUT } from '../data/faqContent';
import AdminPanel from '../components/AdminPanel';
import ConsentCheckbox from '../components/ConsentCheckbox';
import PatrimonyMark from '../components/PatrimonyMark';
import HeroBackdrop from '../components/HeroBackdrop';
import { Tilt, Magnetic } from '../components/motion/Tilt';
import { PROJECTS, WORKS } from '../data/projects';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** Project cover: shows a "coming soon" placeholder until image_url resolves
 * (either unset for now, or the file 404s before it's added to public/). */
function ProjectImage({ src, alt, fit, lang, frameClass = 'aspect-[16/10] w-full', imgExtra = '', pad = 'p-4' }) {
  const [failed, setFailed] = useState(false);
  const showPlaceholder = !src || failed;
  return (
    <div className={`${frameClass} bg-bg overflow-hidden`}>
      {showPlaceholder ? (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-surface-hover/60 text-text-muted">
          <ImageSquare size={28} weight="duotone" />
          <span className="text-[10px] font-bold uppercase tracking-[0.15em]">
            {lang === 'en' ? 'Coming soon' : 'In arrivo'}
          </span>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className={`w-full h-full transition-transform duration-500 group-hover:scale-[1.03] ${imgExtra} ${fit === 'cover' ? 'object-cover' : `object-contain ${pad}`}`}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

/** Compact card: wide thumbnail, title, one grey line. The whole card is the
 * link. Variants: `featured` (the first project: full row below xl, 2 cols x
 * 2 rows at xl, image beside text, long description) and `side` (the two cards
 * stacked next to it at xl, image beside text). Everything else is a plain
 * vertical card. Brand glows (Patrimony, Gabremoku) are the sanctioned ones. */
// The two projects stacked beside the featured one on xl (only if one is featured).
const sideProjectIds = PROJECTS.some((p) => p.featured)
  ? PROJECTS.filter((p) => !p.featured && !p.wide).slice(0, 2).map((p) => p.id)
  : [];

const CARD_GLOW = {
  patrimony: 'patrimony-card-glow',
  'gabremoku-indicators': 'gabremoku-card-glow',
  'velum-obscura': 'velum-card-glow',
  voidflare: 'voidflare-card-glow',
};
// Velum has its own hover ring (index.css), so it opts out of the green accent border/arrow.
const OWN_HOVER = new Set(['velum-obscura']);
// Wide banner cards (`wide: true` in projects.js) reuse the featured layout minus the "featured" label and
// the bento row-span: image beside the text from md, spanning the whole grid row (2 cols on sm, 3 on xl).

function WorkCard({ item, idx, lang, variant = 'default' }) {
  const pick = (field) => (lang === 'en' ? (field?.en || field?.it) : field?.it);
  const featured = variant === 'featured';
  const wide = variant === 'wide';
  const side = variant === 'side';
  const bannerLayout = featured || wide;
  const title = pick(item.title);
  const subtitle = pick(item.subtitle);
  const summary = pick(item.summary);
  const description = pick(item.description);
  const linkText = pick(item.link_url ? item.link_text : null);
  const glow = CARD_GLOW[item.id] || '';
  const ownHover = OWN_HOVER.has(item.id);

  // Literal class strings on purpose: Tailwind only keeps names it can see.
  const frameClass = bannerLayout
    ? 'aspect-[16/10] w-full md:aspect-auto md:relative md:w-[45%] md:flex-shrink-0 md:min-h-[280px]'
    : side
    ? 'aspect-[16/10] w-full xl:aspect-auto xl:relative xl:w-2/5 xl:flex-shrink-0'
    : 'aspect-[16/10] w-full';
  const imgExtra = bannerLayout ? 'md:absolute md:inset-0' : side ? 'xl:absolute xl:inset-0' : '';
  const textClass = bannerLayout
    ? 'p-5 md:p-8 md:border-t-0 md:border-l md:flex md:flex-col md:justify-center md:flex-1'
    : side
    ? 'p-4 xl:border-t-0 xl:border-l xl:flex xl:flex-col xl:justify-center xl:flex-1'
    : 'p-4';
  const layoutClass = bannerLayout ? 'md:flex-row' : side ? 'xl:flex-row' : '';
  const span = featured ? 'sm:col-span-2 xl:row-span-2' : wide ? 'sm:col-span-2 xl:col-span-3' : '';

  const body = (
    <>
      <ProjectImage
        src={item.image_url}
        alt={title}
        fit={item.fit}
        lang={lang}
        frameClass={frameClass}
        imgExtra={imgExtra}
        pad={bannerLayout ? 'p-6 md:p-8' : 'p-4'}
      />
      <div className={`border-t border-line ${textClass}`}>
        {featured ? (
          <p className="text-xs uppercase text-accent mb-2">{lang === 'en' ? 'Featured project' : 'Progetto in evidenza'}</p>
        ) : null}
        <h3 className={`font-outfit font-medium text-text-primary flex items-center justify-between gap-2 ${bannerLayout ? 'text-2xl' : 'text-base'}`}>
          <span>{title}</span>
          {item.link_url ? <ArrowSquareOut size={bannerLayout ? 18 : 14} className={`flex-shrink-0 text-text-muted transition-colors ${ownHover ? 'group-hover:text-text-primary' : 'group-hover:text-accent'}`} aria-hidden="true" /> : null}
        </h3>
        {subtitle ? <p className="text-sm text-text-muted mt-0.5" style={item.subtitle_color ? { color: item.subtitle_color } : undefined}>{subtitle}</p> : null}
        {bannerLayout ? (
          <>
            <p className="text-sm text-text-secondary leading-relaxed mt-3">{description || summary}</p>
            {linkText ? <p className="mt-4 text-sm text-accent">{linkText}</p> : null}
          </>
        ) : summary ? (
          <p className="text-sm text-text-secondary leading-relaxed mt-2 line-clamp-4">{summary}</p>
        ) : null}
      </div>
    </>
  );
  const cardClass = `group flex flex-col ${layoutClass} h-full bg-surface border border-line ${ownHover ? '' : 'hover:border-accent/40'} rounded-xl overflow-hidden transition-colors`;
  return (
    <Tilt max={featured ? 1.5 : 3} className={`${span} ${glow ? `${glow} rounded-xl` : ''}`.trim()}>
      <article
        id={`project-${item.id}`}
        data-testid={`project-card-${item.id}`}
        data-reveal
        style={{ transitionDelay: `${Math.min(idx, 4) * 70}ms` }}
        className="h-full"
      >
        {item.link_url ? (
          <a
            href={item.link_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`project-link-${item.id}`}
            className={cardClass}
          >
            {body}
          </a>
        ) : (
          <div className={cardClass}>{body}</div>
        )}
      </article>
    </Tilt>
  );
}

// Bug fix: these were hardcoded Italian-only, so they never translated when
// the site was switched to English. Each entry now carries both labels.
const skills = [
  { it: 'Programmatore Senior PLC', en: 'Senior PLC Programmer', icon: Cpu },
  { it: 'Problem Solving', en: 'Problem Solving', icon: Lightbulb },
  { it: 'Analisi Fondamentale', en: 'Fundamental Analysis', icon: ChartLineUp },
  { it: 'Macroeconomia', en: 'Macroeconomics', icon: TrendUp },
  { it: 'Value Investing', en: 'Value Investing', icon: Scales },
  { it: 'Risk Management', en: 'Risk Management', icon: ShieldCheck },
  { it: 'Analisi Quantitativa', en: 'Quantitative Analysis', icon: Brain },
  { it: 'Financial Modeling', en: 'Financial Modeling', icon: Briefcase },
];

export default function About() {
  usePageMeta(
    '',
    'Gabriele Denaro: Analista e Programmatore Senior PLC, specializzato in problem solving, value investing e cicli economici. Autore di Liberi di Scegliere.'
  );
  const { t } = useAboutContent();
  const { lang, t: tUi } = useI18n(); // `t` above is the About-page copy; shared UI strings (consent) come from tUi
  const aboutFaq = FAQ_ABOUT[lang] || FAQ_ABOUT.it;
  usePageSchema([
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'Chi è Gabriele Denaro',
      url: 'https://gabrieledenaro.com/',
      mainEntity: { '@type': 'Person', name: 'Gabriele Denaro', url: 'https://gabrieledenaro.com' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: aboutFaq.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
  ]);
  useScrollReveal([]);
  useSectionTone([]);
  useScramble([]);
  const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', email: '', message: '' });
  const [contactConsent, setContactConsent] = useState(false); // privacy notice read; the backend refuses the message without it
  const [contactWebsite, setContactWebsite] = useState(''); // honeypot, kept empty by real visitors
  const contactOpenedAt = useRef(Date.now()); // how long the page stayed open before sending
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // Secret admin: 5 clicks on photo
  const [showAdmin, setShowAdmin] = useState(false);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef(null);

  const handlePhotoClick = useCallback(() => {
    clickCountRef.current += 1;
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    if (clickCountRef.current >= 5) {
      clickCountRef.current = 0;
      setShowAdmin(true);
      return;
    }
    clickTimerRef.current = setTimeout(() => { clickCountRef.current = 0; }, 2000);
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...contactForm, consent: contactConsent, website: contactWebsite, fill_ms: Date.now() - contactOpenedAt.current }),
      });
      const data = await res.json();
      if (res.ok) {
        setSendResult({ success: true, message: data.message });
        setContactForm({ first_name: '', last_name: '', email: '', message: '' });
        setContactConsent(false);
      } else {
        setSendResult({ success: false, message: (typeof data.detail === 'string' && data.detail) || 'Errore nell\'invio' });
      }
    } catch (err) {
      setSendResult({ success: false, message: 'Errore di connessione. Riprova.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Admin Panel */}
      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}

      {/* Hero Section */}
      <header className="relative overflow-hidden" data-tone-base>
        <HeroBackdrop />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 hero-stagger">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-accent mb-4">
                {t('about.hero.overline', 'Analista & Investitore')}
              </p>
              <h1 className="font-outfit font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-gradient-animated" data-testid="about-hero-name">
                {t('about.hero.name_line1', 'Gabriele')}<br />{t('about.hero.name_line2', 'Denaro')}
              </h1>
              <p className="text-text-secondary text-lg mt-6 max-w-xl leading-relaxed whitespace-pre-line">
                {t('about.hero.tagline', "Vengo dall'automazione industriale, non dalla finanza. Studio i mercati con lo stesso metodo con cui si cerca un guasto: dati alla mano, un'ipotesi alla volta.")}
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                {[
                  { href: 'https://www.linkedin.com/in/gabriele-denaro/', testId: 'social-linkedin', Icon: LinkedinLogo, label: 'LinkedIn' },
                  { href: 'https://www.tiktok.com/@denaro.gabriele', testId: 'social-tiktok', Icon: TiktokLogo, label: 'TikTok' },
                ].map(({ href, testId, Icon, label }) => (
                  <Magnetic key={testId}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={testId}
                      className="flex items-center gap-2 px-4 py-2.5 bg-surface border border-line rounded-sm text-text-secondary hover:text-accent hover:border-accent/30 transition-colors text-sm"
                    >
                      <Icon size={18} weight="bold" />
                      {label}
                    </a>
                  </Magnetic>
                ))}
                <Magnetic>
                  {/* Scrolls to the Patrimony card in "I miei progetti" (which links out) instead of leaving the site. */}
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.getElementById('project-patrimony');
                      if (!el) return;
                      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
                      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
                    }}
                    data-testid="social-patrimony"
                    className="patrimony-cta btn-sheen flex items-center gap-2 min-h-[42px] px-[15.5px] py-[9.5px] rounded-sm text-text-primary text-sm font-semibold"
                  >
                    <PatrimonyMark size={18} />
                    Patrimony
                  </button>
                </Magnetic>
              </div>
            </div>
            <div className="lg:col-span-5 flex justify-center lg:justify-end lg:translate-y-8">
              <div
                className="relative group/portrait animate-fade-in-up"
                style={{ animationDelay: '0.35s', animationFillMode: 'both' }}
                onMouseMove={(e) => {
                  const glow = e.currentTarget.querySelector('[data-portrait-glow]');
                  if (!glow) return;
                  const r = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - r.left;
                  const y = e.clientY - r.top;
                  glow.style.background = `radial-gradient(circle 200px at ${x}px ${y}px, rgba(0, 216, 146, 0.35), transparent 70%)`;
                }}
                onMouseLeave={(e) => {
                  const glow = e.currentTarget.querySelector('[data-portrait-glow]');
                  if (glow) glow.style.background = 'none';
                }}
              >
                {/* Depth panel peeking out behind the portrait, breaks the flat single-plane look */}
                <div
                  aria-hidden="true"
                  className="absolute -top-6 -left-6 w-64 h-80 lg:w-72 lg:h-96 rounded-2xl border border-accent/20 bg-accent-deep/60 -z-10"
                />
                {/* Cursor-following border glow */}
                <div
                  data-portrait-glow
                  aria-hidden="true"
                  className="absolute -inset-[2px] rounded-2xl pointer-events-none opacity-0 group-hover/portrait:opacity-100 transition-opacity duration-300"
                />
                <div className="relative w-64 h-80 lg:w-72 lg:h-96 rounded-2xl overflow-hidden border border-line grayscale hover:grayscale-0 transition-all duration-700 cursor-default" onClick={handlePhotoClick}>
                  <img
                    src="/portrait.jpg"
                    alt="Gabriele Denaro - Programmatore Senior PLC, Problem Solver e Investitore Value"
                    className="w-full h-full object-cover"
                    data-testid="about-portrait"
                    loading="eager"
                  />
                </div>
                <Link
                  to="/ciclo-economico"
                  aria-label={lang === 'en' ? 'Open the economic cycle dashboard' : 'Apri la dashboard del ciclo economico'}
                  title={lang === 'en' ? 'Economic cycle dashboard' : 'Dashboard del ciclo economico'}
                  data-testid="portrait-cycle-link"
                  className="absolute -bottom-3 -right-3 w-16 h-16 bg-accent-deep border border-accent/30 flex items-center justify-center rounded-2xl transition-transform duration-300 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  <ChartLineUp size={28} weight="duotone" className="text-accent" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Su di me — full-bleed band, first "room" */}
      <div className="bg-surface/20 border-y border-line py-16 lg:py-24">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12" id="about-bio-section">
          <article className="lg:col-span-7" data-testid="about-bio-section">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-accent mb-3" data-reveal>{t('about.bio.overline', 'Su di me')}</p>
            <h2 className="font-outfit font-bold text-3xl tracking-tight text-text-primary mb-6" data-reveal style={{ transitionDelay: '60ms' }}>
              {t('about.bio.title', 'Analista, programmatore senior PLC e investitore di lungo periodo')}
            </h2>
            <div className="space-y-4 text-text-secondary leading-relaxed">
              <p className="whitespace-pre-line" data-reveal style={{ transitionDelay: '120ms' }}>{t('about.bio.p1')}</p>
              <p className="whitespace-pre-line" data-reveal style={{ transitionDelay: '200ms' }}>{t('about.bio.p2')}</p>
              <p className="whitespace-pre-line" data-reveal style={{ transitionDelay: '280ms' }}>{t('about.bio.p3')}</p>
            </div>
          </article>

          {/* Competenze */}
          <aside className="lg:col-span-5" data-testid="about-skills-section">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-accent mb-3" data-reveal>{t('about.skills.overline', 'Competenze')}</p>
            <h2 className="font-outfit font-bold text-3xl tracking-tight text-text-primary mb-6" data-reveal style={{ transitionDelay: '60ms' }}>
              {t('about.skills.title', 'Aree di competenza')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {skills.map(({ it: nameIt, en: nameEn, icon: Icon }, i) => (
                <Tilt key={nameIt} max={7}>
                  <div
                    data-testid={`skill-${nameIt.toLowerCase().replace(/\s/g, '-')}`}
                    data-reveal
                    style={{ transitionDelay: `${120 + Math.min(i, 5) * 70}ms` }}
                    className="flex items-center gap-3 p-3 bg-surface border border-line rounded-sm hover:border-accent/20 hover:bg-surface-hover transition-colors"
                  >
                    <div className="w-9 h-9 rounded-sm bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Icon size={18} weight="duotone" className="text-accent" />
                    </div>
                    <span className="text-text-primary text-sm font-medium">{lang === 'en' ? nameEn : nameIt}</span>
                  </div>
                </Tilt>
              ))}
            </div>
          </aside>
        </section>
      </div>

      {/* Filosofia di Investimento — full-bleed, second "room": ambient backdrop, bigger moment */}
      <div className="relative overflow-hidden py-24 lg:py-32" id="about-philosophy-section" data-tone="deep" data-testid="about-philosophy-section">
        <HeroBackdrop compact />
        <section className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-reveal>
          <blockquote className="relative">
            <div className="text-accent/20 text-8xl font-outfit font-black absolute -top-8 -left-2 select-none">"</div>
            <p className="text-text-primary text-2xl lg:text-4xl font-outfit font-medium leading-snug pl-8 pr-4 italic whitespace-pre-line">
              {t('about.philosophy.quote')}
            </p>
            <footer className="mt-8 pl-8">
              <p className="text-accent font-mono text-sm font-bold">{t('about.philosophy.author', 'Warren Buffett')}</p>
              <p className="text-text-muted text-xs mt-0.5">{t('about.philosophy.tagline', 'Il principio da cui parte ogni analisi di questo sito')}</p>
            </footer>
          </blockquote>
        </section>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-tone-base>
        {/* I miei progetti */}
        <section id="about-projects-section" data-testid="about-projects-section">
          <h2 className="font-outfit font-bold text-3xl tracking-tight text-text-primary mb-6" data-reveal data-scramble>
            {t('about.projects.overline', 'I miei progetti')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {PROJECTS.map((p, idx) => (
              <WorkCard
                key={p.id}
                item={p}
                idx={idx}
                lang={lang}
                variant={p.featured ? 'featured' : p.wide ? 'wide' : sideProjectIds.includes(p.id) ? 'side' : 'default'}
              />
            ))}
          </div>
        </section>

        {/* Lavori: incarichi fatti per altri, non progetti miei */}
        <section className="mt-20" id="about-works-section" data-testid="about-works-section">
          <h2 className="font-outfit font-bold text-3xl tracking-tight text-text-primary mb-6" data-reveal>
            {lang === 'en' ? 'Work' : 'Lavori'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {WORKS.map((c, idx) => (
              <WorkCard key={c.id} item={c} idx={idx} lang={lang} />
            ))}
          </div>
        </section>

        {/* FAQ — visible content matching the FAQPage schema injected via usePageSchema */}
        <section className="mt-24" id="about-faq-section" data-testid="about-faq-section" data-reveal>
          <h2 className="font-outfit font-bold text-3xl tracking-tight text-text-primary mb-6">
            {lang === 'en' ? 'FAQ' : 'Domande frequenti'}
          </h2>
          <dl className="space-y-4">
            {aboutFaq.map(({ q, a }) => (
              <div key={q} className="bg-surface border border-line rounded-sm p-5">
                <dt className="text-text-primary font-semibold mb-1.5">{q}</dt>
                <dd className="text-text-secondary text-sm leading-relaxed">{a}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Contatti */}
        <section className="mt-24" id="about-contact-section" data-testid="about-contact-section" data-reveal>
          <div className="bg-surface border border-line rounded-sm p-8 lg:p-10">
            <h3 className="font-outfit font-bold text-2xl tracking-tight text-text-primary mb-2">
              {t('about.contact.title', 'Scrivimi')}
            </h3>
            <p className="text-text-secondary text-sm mb-6 whitespace-pre-line">
              {t('about.contact.subtitle', 'Hai una domanda, una proposta o vuoi semplicemente entrare in contatto? Compila il form qui sotto.')}
            </p>

            <form onSubmit={handleContactSubmit} className="space-y-4 max-w-2xl" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first_name" className="text-text-muted text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">
                    {lang === 'en' ? 'First name' : 'Nome'} *
                  </label>
                  <input
                    id="first_name"
                    maxLength={100}
                    data-testid="contact-first-name"
                    type="text"
                    required
                    value={contactForm.first_name}
                    onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                    className="w-full bg-bg border border-line rounded-sm px-3 py-2.5 text-text-primary text-sm font-mono placeholder:text-text-muted focus:outline-none focus:border-accent/50 transition-colors"
                    placeholder={lang === 'en' ? 'Your first name' : 'Il tuo nome'}
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className="text-text-muted text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">
                    {lang === 'en' ? 'Last name' : 'Cognome'} *
                  </label>
                  <input
                    id="last_name"
                    maxLength={100}
                    data-testid="contact-last-name"
                    type="text"
                    required
                    value={contactForm.last_name}
                    onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                    className="w-full bg-bg border border-line rounded-sm px-3 py-2.5 text-text-primary text-sm font-mono placeholder:text-text-muted focus:outline-none focus:border-accent/50 transition-colors"
                    placeholder={lang === 'en' ? 'Your last name' : 'Il tuo cognome'}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="text-text-muted text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">
                  Email *
                </label>
                <input
                  id="email"
                  maxLength={200}
                  data-testid="contact-email"
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full bg-bg border border-line rounded-sm px-3 py-2.5 text-text-primary text-sm font-mono placeholder:text-text-muted focus:outline-none focus:border-accent/50 transition-colors"
                  placeholder="name@email.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="text-text-muted text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">
                  {lang === 'en' ? 'Message' : 'Messaggio'} *
                </label>
                <textarea
                  id="message"
                  maxLength={4000}
                  data-testid="contact-message"
                  required
                  rows={5}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="w-full bg-bg border border-line rounded-sm px-3 py-2.5 text-text-primary text-sm font-mono placeholder:text-text-muted focus:outline-none focus:border-accent/50 transition-colors resize-none"
                  placeholder={lang === 'en' ? 'Write your message here...' : 'Scrivi qui il tuo messaggio...'}
                />
              </div>

              {/* Honeypot: off-screen rather than display:none, so bots that parse the DOM still fill it. */}
              <input
                type="text"
                name="website"
                value={contactWebsite}
                onChange={(e) => setContactWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="sr-only"
              />

              <ConsentCheckbox
                id="contact-consent"
                testId="contact-consent"
                checked={contactConsent}
                onChange={setContactConsent}
                to="/privacy"
                linkText={tUi('consent.privacy_link')}
              >
                {tUi('consent.privacy_lead')}
              </ConsentCheckbox>

              <div className="flex items-center gap-4">
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={sending || !contactConsent}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-deep text-accent border border-accent-deep font-outfit font-bold rounded-sm hover:bg-accent-deep-hover transition-colors text-sm disabled:opacity-50"
                >
                  {sending ? (
                    <SpinnerGap size={16} className="animate-spin" />
                  ) : (
                    <PaperPlaneTilt size={16} weight="bold" />
                  )}
                  {sending ? (lang === 'en' ? 'Sending...' : 'Invio in corso...') : (lang === 'en' ? 'Send message' : 'Invia Messaggio')}
                </button>

                {sendResult && (
                  <div className={`flex items-center gap-2 text-sm ${sendResult.success ? 'text-accent-green' : 'text-accent-red'}`} data-testid="contact-result">
                    {sendResult.success ? <CheckCircle size={16} weight="fill" /> : <Warning size={16} weight="fill" />}
                    {sendResult.message}
                  </div>
                )}
              </div>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}
